import type { MpUserSelfSettings } from '../lib/mpUserSelfPublish';

type Props = {
  settings: MpUserSelfSettings;
  onChange: (patch: Partial<MpUserSelfSettings>) => void;
};

export function MiniprogramReviewSettingsCard({ settings, onChange }: Props) {
  const rows: Array<{ key: keyof MpUserSelfSettings; label: string; hint: string }> = [
    {
      key: 'review_entry',
      label: '发布闲置审核',
      hint: '开启后，用户提交的闲置信息需管理员审核通过后才会展示。',
    },
    {
      key: 'review_entry_edit',
      label: '修改闲置审核',
      hint: '开启后，用户修改已发布信息需审核通过后才会更新展示；关闭则修改后立即生效。',
    },
    {
      key: 'review_nickname',
      label: '昵称修改审核',
      hint: '开启后，用户修改昵称需审核通过后才会更新展示。',
    },
    {
      key: 'review_avatar',
      label: '头像修改审核',
      hint: '开启后，用户修改头像需审核通过后才会更新展示。',
    },
  ];

  const nonSecondSubRows: Array<{
    key: 'non_secondhand_review_job' | 'non_secondhand_review_housing' | 'non_secondhand_review_dating';
    label: string;
    hint: string;
  }> = [
    {
      key: 'non_secondhand_review_job',
      label: '疑似招聘信息自动进审核',
      hint: '检测到兼职、日结、招工等招聘类表述时自动进入待审。',
    },
    {
      key: 'non_secondhand_review_housing',
      label: '疑似房产信息自动进审核',
      hint: '检测到出租、整租、房源等房产类表述时自动进入待审。',
    },
    {
      key: 'non_secondhand_review_dating',
      label: '疑似相亲信息自动进审核',
      hint: '检测到相亲、征婚、交友等表述时自动进入待审。',
    },
  ];

  const allNonSecondOn = !!settings.non_secondhand_review_all;

  const onToggleReviewAll = (checked: boolean) => {
    if (checked) {
      onChange({
        non_secondhand_review_all: true,
        non_secondhand_review_job: false,
        non_secondhand_review_housing: false,
        non_secondhand_review_dating: false,
      });
      return;
    }
    onChange({ non_secondhand_review_all: false });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-1 text-base font-semibold text-slate-900">用户操作审核</h3>
      <p className="mb-4 text-sm text-slate-600">
        控制微信小程序端用户提交类操作是否进入待审状态；关闭后对应操作将立即生效。
      </p>
      <div className="space-y-4">
        {rows.map((row) => (
          <label key={row.key} className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              checked={!!settings[row.key]}
              onChange={(e) => onChange({ [row.key]: e.target.checked })}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800">{row.label}</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-500">{row.hint}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="mt-5 border-t border-slate-100 pt-5">
        <h4 className="mb-3 text-sm font-semibold text-slate-800">重复发布检测</h4>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={!!settings.dedupe_entry_enabled}
            onChange={(e) => onChange({ dedupe_entry_enabled: e.target.checked })}
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-slate-800">疑似重复自动进审核</span>
            <span className="mt-1 block text-xs leading-relaxed text-slate-500">
              同一用户发布时，若联系电话相同、标题/描述高度相似且图片有重叠，不拦截提交，自动进入待审队列，由管理员决定通过或删除。不影响用户正常发布体验。
            </span>
          </span>
        </label>
        {settings.dedupe_entry_enabled ? (
          <label className="mt-3 block text-sm text-slate-700">
            <span className="mb-1 block text-xs font-medium text-slate-600">比对时间范围（天）</span>
            <input
              type="number"
              min={30}
              max={365}
              className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={settings.dedupe_lookback_days ?? 120}
              onChange={(e) =>
                onChange({
                  dedupe_lookback_days: Math.min(365, Math.max(30, Number(e.target.value) || 120)),
                })
              }
            />
          </label>
        ) : null}
      </div>
      <div className="mt-5 border-t border-slate-100 pt-5">
        <h4 className="mb-1 text-sm font-semibold text-slate-800">非二手信息进入审核</h4>
        <p className="mb-3 text-xs leading-relaxed text-slate-500">
          本平台面向二手闲置发布。检测到招聘、房产、相亲等非闲置类内容时，可自动进入待审，由管理员决定是否上线。
        </p>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 hover:bg-slate-50">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={allNonSecondOn}
            onChange={(e) => onToggleReviewAll(e.target.checked)}
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-slate-800">疑似非二手信息全部进审核</span>
            <span className="mt-1 block text-xs leading-relaxed text-slate-500">
              开启后，未体现闲置/二手转让特征的内容一律进入待审；下方分类选项将自动关闭。
            </span>
          </span>
        </label>
        <div className={`mt-3 space-y-3 ${allNonSecondOn ? 'opacity-50' : ''}`}>
          {nonSecondSubRows.map((row) => (
            <label
              key={row.key}
              className={`flex items-start gap-3 rounded-lg border border-slate-100 p-3 ${
                allNonSecondOn ? 'cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:bg-slate-50'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 disabled:cursor-not-allowed"
                checked={!!settings[row.key]}
                disabled={allNonSecondOn}
                onChange={(e) => onChange({ [row.key]: e.target.checked })}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-800">{row.label}</span>
                <span className="mt-1 block text-xs leading-relaxed text-slate-500">{row.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
